/* eslint-disable react/no-array-index-key */
import React, { PropsWithChildren, useMemo } from 'react'

import * as S from './styles'
import { Theme } from '@/styles/themes'

export type TypographyProps = PropsWithChildren<{
  /**
   * Color of the typography
   *
   * @default 'white'
   *
   * @example
   * ```tsx
   * <Typography color="primary" />
   * ```
   *
   */
  color?: keyof Theme['colors']

  /**
   * Weight of the typography
   *
   * @default 'regular'
   *
   * @example
   * ```tsx
   * <Typography weight="bold" />
   * ```
   *
   */
  weight?: keyof Theme['fontWeights']

  /**
   * Size of the typography
   *
   * @default 'b1'
   *
   * @example
   * ```tsx
   * <Typography size="b2" />
   * ```
   *
   */
  size?: keyof Theme['fontSizes']

  /**
   * Family of the text
   *
   * @default 'primary'
   *
   * @example
   * ```tsx
   * <Typography family="secondary" />
   * ```
   *
   */
  family?: keyof Theme['fontFamily']

  /**
   * The class name of the typography
   *
   * @example
   * ```tsx
   * <Typography className="my-class" />
   * ```
   *
   */
  className?: string

  /**
   * The alignment of the text
   *
   * @default 'left'
   * @example
   * ```tsx
   * <Text align="left" />
   * ```
   *
   */
  align?: 'left' | 'center' | 'right'
  lineHeight?: keyof Theme['lineHeight']
  /**
   * Function to be called when the text is clicked
   *
   * @example
   * ```tsx
   * <Text onClick={() => console.log('Text clicked')} />
   * ```
   *
   */
  onClick?: () => void
}>

export const Typography: React.FC<TypographyProps> = ({
  children,
  color = 'white',
  weight = 'regular',
  size = 'b1',
  family = 'primary',
  lineHeight = 'b1',
  className,
  onClick,
  align = 'left',
}) => {
  const headingRegex = useMemo(() => /^h[1-6]$/, [])
  const isHeading = useMemo(() => headingRegex.test(size), [size, headingRegex])

  const lineBrokenChildren = useMemo(() => {
    if (typeof children === 'string') {
      return children.split('<br>')
    }

    return [children]
  }, [children])

  return (
    <>
      {lineBrokenChildren.map((child, index) => (
        <S.Text
          key={`text-${index}`}
          as={isHeading ? size : `p`}
          color={color}
          weight={weight}
          size={size}
          family={family}
          lineHeight={lineHeight}
          className={className}
          onClick={onClick}
          hasOnClick={!!onClick}
          align={align}
        >
          {child}
        </S.Text>
      ))}
    </>
  )
}
